import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "@/utils/analytics";

export type Line =
  | { type: "sys"; text: string }
  | { type: "in"; text: string }
  | { type: "out"; text: string }
  | { type: "err"; text: string };

const STORAGE_KEY = "devbloom.assistant.history";
const HISTORY_CAP = 40;

function readStorage(): Line[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStorage(lines: Line[]): void {
  if (typeof window === "undefined") return;
  try {
    const capped = lines.slice(-HISTORY_CAP);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(capped));
  } catch {
    // storage quota or private mode — fail silently
  }
}

function clearStorage(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // fail silently
  }
}

interface AssistantContextValue {
  history: Line[];
  busy: boolean;
  streaming: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  reset: () => void;
  ask: (q: string, source?: "typed" | "suggestion") => Promise<void>;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t, i18n } = useTranslation();

  const [history, setHistory] = useState<Line[]>(() => {
    const stored = readStorage();
    if (stored.length > 0) return stored;
    return [
      { type: "sys", text: t("aiFeature.terminal.systemLine1") },
      { type: "sys", text: t("aiFeature.terminal.systemLine2") },
    ];
  });
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const historyRef = useRef(history);
  historyRef.current = history;
  const ctaTrackedRef = useRef(history.some((l) => l.type === "out"));

  useEffect(() => {
    writeStorage(history);
    const hasAnswer = history.some((l) => l.type === "out");
    if (hasAnswer && !ctaTrackedRef.current) {
      ctaTrackedRef.current = true;
      trackEvent("assistant_cta_shown");
    }
  }, [history]);

  const open = useCallback(() => {
    setIsOpen(true);
    trackEvent("assistant_open", { source: "launcher" });
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        trackEvent("assistant_open", { source: "launcher" });
      }
      return !prev;
    });
  }, []);

  const reset = useCallback(() => {
    const initial: Line[] = [
      { type: "sys", text: t("aiFeature.terminal.systemLine1") },
      { type: "sys", text: t("aiFeature.terminal.systemLine2") },
    ];
    setHistory(initial);
    ctaTrackedRef.current = false;
    clearStorage();
  }, [t]);

  const ask = useCallback(
    async (q: string, source: "typed" | "suggestion" = "typed") => {
      if (!q.trim() || busy) return;

      trackEvent("assistant_ask", { source });
      const newHistory: Line[] = [
        ...historyRef.current,
        { type: "in", text: q },
      ];
      setHistory(newHistory);
      setBusy(true);
      setStreaming(false);

      const contents = newHistory
        .filter(
          (l): l is Extract<Line, { type: "in" | "out" }> =>
            l.type === "in" || l.type === "out"
        )
        .map((l) => ({
          role: l.type === "in" ? "user" : "model",
          parts: [{ text: l.text }],
        }));

      try {
        const res = await fetch("/api/ask", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contents, locale: i18n.language }),
        });

        if (!res.ok || !res.body) {
          const data = await res.json().catch(() => ({}));
          throw new Error(
            (data as { error?: string }).error ?? `HTTP ${res.status}`
          );
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let accumulated = "";
        let started = false;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          accumulated += decoder.decode(value, { stream: true });
          const text = accumulated;

          if (!started) {
            setStreaming(true);
            setHistory((h) => [...h, { type: "out", text }]);
            started = true;
          } else {
            setHistory((h) => {
              const next = [...h];
              next[next.length - 1] = { type: "out", text };
              return next;
            });
          }
        }

        if (!started) {
          setHistory((h) => [
            ...h,
            { type: "out", text: t("aiFeature.terminal.noResponse") },
          ]);
        }
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Request failed";
        setHistory((h) => [...h, { type: "err", text: msg }]);
      } finally {
        setBusy(false);
        setStreaming(false);
      }
    },
    [busy, i18n.language, t]
  );

  return (
    <AssistantContext.Provider
      value={{ history, busy, streaming, isOpen, open, close, toggle, reset, ask }}
    >
      {children}
    </AssistantContext.Provider>
  );
};

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error("useAssistant must be used within AssistantProvider");
  return ctx;
}
